'use client';

import { useLocale } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useLeaveGuard } from '@/components/ui/dirty-guard';
import { routing } from '@/i18n/routing';

/** The locale switch both the footer switcher and Settings › Preferences use: cookie, local storage and a guarded `router.replace`. */
export function useLocaleSwitch(): { locale: string; switchTo: (next: string) => void } {
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const { guard } = useLeaveGuard();

  function setLocaleCookie(nextLocale: string) {
    try {
      document.cookie = `NEXT_LOCALE=${nextLocale}; Path=/; Max-Age=31536000; SameSite=Lax`;
      // Local storage as a secondary hint for client-side routing
      window.localStorage.setItem('preferredLocale', nextLocale);
    } catch {}
  }

  function switchTo(nextLocale: string) {
    const currentPath = pathname || '/';
    const parts = currentPath.split('/');
    // Ensure first segment is locale per localePrefix: "always"
    if (parts.length > 1) {
      parts[1] = nextLocale;
    }
    const target = parts.join('/') || '/';
    // Staying (Keep editing) leaves the cookie and the controlled select on the current locale.
    void guard(() => {
      setLocaleCookie(nextLocale);
      router.replace(target);
    });
  }

  return { locale, switchTo };
}

/** Endonym per locale, so every language names itself in its own script. */
export const LOCALE_LABELS: Record<string, string> = {
  'en-AU': 'English',
  'zh-TW': '繁體中文',
  'ja-JP': '日本語',
  'fr-FR': 'Français',
};

export function localeLabel(loc: string): string {
  return LOCALE_LABELS[loc] ?? loc;
}

export default function LanguageSwitcher() {
  const { locale, switchTo } = useLocaleSwitch();

  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <span className="sr-only">Language</span>
      <select
        className="border-border bg-card dark:bg-card text-foreground h-9 rounded-md border px-2 pr-6 outline-none"
        value={locale}
        onChange={(e) => switchTo(e.target.value)}
      >
        {routing.locales.map((loc) => (
          <option key={loc} value={loc}>
            {localeLabel(loc)}
          </option>
        ))}
      </select>
    </label>
  );
}
